import React, { useState, useEffect } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { lapData } from "../../../types/virtualLoops";
import { useDispatch } from "react-redux";
import { getPreferredFormattedTimeSeconds } from "../../../utils/preferences";
import {
  setDriverFilters,
  setHighlightedLoop,
  setOutInLaps,
  setPowerModeFilters,
  setPushLapsOnlyFilter,
  setSelectedSwitches,
  setSessionFilters,
} from "../../../state/virtualLoopsReducer";
import { getColourMap } from "../../../utils";
import { SetVirtualLoopsLapReal } from "../../../utils/graphql/hooks/virtualLoopsHooks";

const SWITCH_NAMES_Gen3 = {
  0: "ERROR",
  1: "R2",
  2: "R3",
  3: "R4",
  4: "TEST",
  5: "OUT",
  6: "P300",
  7: "X1",
  8: "X2",
  9: "BO",
  10: "R1",
};

const SWITCH_NAMES_Gen4 = {
  1: "Out",
  2: "BURNOUT",
  3: "SAFETY_CAR",
  4: "FREE_PRACTICE_1",
  5: "FREE_PRACTICE_2",
  6: "FREE_PRACTICE_3",
  7: "FREE_PRACTICE_4",
  8: "HIGH_POWER_1",
  9: "HIGH_POWER_2",
  10: "HIGH_POWER_3",
  11: "HIGH_POWER_4",
  12: "RACE1",
  13: "RACE2",
  14: "RACE3",
  15: "RACE4",
  16: "RACE5",
  17: "RACE6",
  18: "RACE7",
  19: "RACE8",
  20: "RACE9",
};

function LapsTable() {
  const dispatch = useDispatch();

  const setVirtualLoopsLapReal = SetVirtualLoopsLapReal();

  const busy = useSelector(
    (state: RootState) => state.virtualLoops?.busy,
    isEqual,
  );
  const filteredData = useSelector(
    (state: RootState) => state.virtualLoops?.filteredData,
    isEqual,
  );
  const data = useSelector(
    (state: RootState) => state.virtualLoops?.data,
    isEqual,
  );

  const sessionFilter = useSelector(
    (state: RootState) => state.virtualLoops?.lapFilters?.session || "all",
  );
  const powerFilter = useSelector(
    (state: RootState) => state.virtualLoops?.lapFilters?.powerMode || "all",
  );
  const driverFilter = useSelector(
    (state: RootState) => state.virtualLoops?.lapFilters?.driver || "all",
  );
  const pushLapsOnly = useSelector(
    (state: RootState) => state.virtualLoops?.lapFilters?.pushLapsOnly || false,
  );
  const highlightedLoop = useSelector(
    (state: RootState) => state.virtualLoops?.highlightedLoop || "",
  );
  const selectedSwitches = useSelector(
    (state: RootState) =>
      state.virtualLoops?.lapFilters?.selectedSwitches || [],
  );
  const outInLaps = useSelector(
    (state: RootState) => state.virtualLoops?.lapFilters?.outInLaps || false,
  );
  const generation = useSelector(
    (state: RootState) => state.virtualLoops?.generation || "Gen3",
  );
  const highPowerMode = generation === "Gen4" ? "600" : "350";

  const SWITCH_NAMES =
    generation === "Gen4" ? SWITCH_NAMES_Gen4 : SWITCH_NAMES_Gen3;

  const [colourMapEnabled, setColourMapEnabled] = useState(false);
  const [optionsVisible, setOptionsVisible] = useState(false);

  useEffect(() => {
    const allSwitches = Object.keys(SWITCH_NAMES).map(Number);
    dispatch(setSelectedSwitches(allSwitches));
  }, [generation, SWITCH_NAMES, dispatch]);

  const virtualLoopNames = Array.from(
    new Set(
      Object.keys(data || {}).flatMap((key) =>
        Array.isArray(data?.[key])
          ? data[key].flatMap((item: lapData) =>
              item?.virtualLoops ? Object.keys(item.virtualLoops) : [],
            )
          : [],
      ),
    ),
  );

  const driverNames = Array.from(
    new Set(
      Object.keys(data || {}).flatMap((key) =>
        Array.isArray(data?.[key])
          ? data[key].map((item: lapData) => item?.driver).filter(Boolean)
          : [],
      ),
    ),
  ).sort();

  const sessionNames = Array.from(
    new Set(
      Object.keys(data || {}).flatMap((key) =>
        Array.isArray(data?.[key])
          ? data[key].map((item: lapData) => item?.session).filter(Boolean)
          : [],
      ),
    ),
  ).sort();

  const powerModes = Array.from(
    new Set(
      Object.keys(data || {}).flatMap((key) =>
        Array.isArray(data?.[key])
          ? data[key].map((item: lapData) => item?.powerMode).filter(Boolean)
          : [],
      ),
    ),
  ).sort();

  const fastestValues: Record<string, number> = {};

  const getLoopTimeRanges = () => {
    const ranges: Record<string, { min: number; max: number }> = {};

    if (!filteredData) return ranges;

    Object.values(filteredData)
      .flat()
      .forEach((lap) => {
        if (!lap.virtualLoops || lap.real === false) return;

        Object.entries(lap.virtualLoops).forEach(([loopName, time]) => {
          if (typeof time === "number") {
            if (!ranges[loopName]) {
              ranges[loopName] = { min: time, max: time };
            } else {
              ranges[loopName].min = Math.min(ranges[loopName].min, time);
              ranges[loopName].max = Math.max(ranges[loopName].max, time);
            }
          }
        });
      });

    return ranges;
  };

  const loopTimeRanges = getLoopTimeRanges();

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <div className={styles.basicFilters}>
          <div className={styles.select}>
            <select
              value={sessionFilter}
              onChange={(e) => dispatch(setSessionFilters(e.target.value))}
            >
              <option value="all">All Sessions</option>
              {sessionNames.map((session) => (
                <option key={session} value={session}>
                  {session}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.select}>
            <select
              value={powerFilter}
              onChange={(e) => dispatch(setPowerModeFilters(e.target.value))}
            >
              <option value="all">Any kW</option>
              {powerModes.map((powerMode) => (
                <option key={powerMode} value={powerMode}>
                  {powerMode + " kW"}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.select}>
            <select
              value={driverFilter}
              onChange={(e) => dispatch(setDriverFilters(e.target.value))}
            >
              <option value="all">All Drivers</option>
              {driverNames.map((driver) => (
                <option key={driver} value={driver}>
                  {driver}
                </option>
              ))}
            </select>
          </div>
          <div>
            <input
              type="checkbox"
              id="pushLapsOnly"
              name="pushLapsOnly"
              checked={pushLapsOnly}
              onChange={(e) =>
                dispatch(setPushLapsOnlyFilter(e.target.checked))
              }
            />
            <label htmlFor="pushLapsOnly">Push Laps Only</label>
          </div>
          <div>
            <input
              type="checkbox"
              id="inOutLaps"
              name="inOutLaps"
              checked={outInLaps}
              onChange={(e) => dispatch(setOutInLaps(e.target.checked))}
            />
            <label htmlFor="inOutLaps">In/Out Laps</label>
          </div>
          <div>
            <input
              type="checkbox"
              id="colourMap"
              name="colourMap"
              checked={colourMapEnabled}
              onChange={(e) => setColourMapEnabled(e.target.checked)}
            />
            <label htmlFor="colourMap">Colour Map</label>
          </div>
          <button onClick={() => setOptionsVisible(!optionsVisible)}>
            {optionsVisible ? "Hide" : "Show"} Advanced
          </button>
        </div>

        <div className={styles.advancedFilters}>
          {optionsVisible && (
            <>
              <div className={styles.switchCheckboxes}>
                <div>Switch:</div>
                {Object.entries(SWITCH_NAMES)
                  .filter(([_, value]) => value !== "ERROR")
                  .map(([key, value]) => (
                    <div key={key}>
                      <input
                        type="checkbox"
                        id={`switch_${key}`}
                        name={`switch_${key}`}
                        checked={selectedSwitches.includes(Number(key))}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          const newSelectedSwitches = isChecked
                            ? [...selectedSwitches, Number(key)]
                            : selectedSwitches.filter((k) => k !== Number(key));
                          dispatch(setSelectedSwitches(newSelectedSwitches));
                        }}
                      />
                      <label htmlFor={`switch_${key}`}>{value}</label>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles.tableDIV}>
        {busy && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner}></div>
          </div>
        )}
        <table className={styles.table}>
          <thead className={styles.sticky}>
            <tr className={styles.titleRow}>
              <th colSpan={virtualLoopNames.length + 3}>Virtual Loops Table</th>
            </tr>
            <tr>
              <th>Lap</th>
              <th>Lap Time</th>
              <th>Power</th>
              {virtualLoopNames.map((loopName) => (
                <th
                  key={loopName}
                  onClick={() => dispatch(setHighlightedLoop(loopName))}
                  className={
                    highlightedLoop === loopName
                      ? styles.highlighted
                      : undefined
                  }
                  style={{ cursor: "pointer" }}
                >
                  {loopName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData &&
              Object.keys(filteredData)
                .filter(
                  (_) =>
                    filteredData[_] &&
                    Array.isArray(filteredData[_]) &&
                    filteredData[_].length > 0,
                )
                .map((sessionKey, _unused) => (
                  <React.Fragment key={sessionKey}>
                    <tr>
                      <td
                        colSpan={virtualLoopNames.length + 3}
                        className={styles.spacerRow}
                      >
                        {sessionKey}
                      </td>
                    </tr>
                    {filteredData[sessionKey].map((lapData) => {
                      let lastLoopNull = false;
                      const lt = lapData?.lapTime;
                      const hasPitLane = lapData?.inLap || lapData?.outLap;
                      let isFastest = false;
                      if (
                        lapData.real !== false &&
                        typeof lt === "number" &&
                        !hasPitLane &&
                        (fastestValues["lap"] === undefined ||
                          lt < fastestValues["lap"])
                      ) {
                        fastestValues["lap"] = lt;
                        isFastest = true;
                      }

                      const isGreyedOut = lapData.real === false;

                      return (
                        <tr
                          key={`${sessionKey}_lap${lapData.lap}_${lapData.lapTime}`}
                          className={
                            isGreyedOut ? styles.greyedOutLap : undefined
                          }
                        >
                          <td
                            onClick={() => {
                              const action = lapData.real
                                ? "disable"
                                : "enable";
                              const confirmed = window.confirm(
                                `Are you sure you want to ${action} lap ${lapData.lap}?`,
                              );
                              if (confirmed) {
                                setVirtualLoopsLapReal({
                                  variables: {
                                    lapReal: {
                                      session: sessionKey,
                                      lapNumber: lapData.lap,
                                      real: !lapData.real,
                                    },
                                  },
                                });
                              }
                            }}
                          >
                            {lapData.lap}
                          </td>
                          <td
                            className={
                              !isGreyedOut && isFastest
                                ? styles.fastestCorner
                                : undefined
                            }
                          >
                            {getPreferredFormattedTimeSeconds(lapData.lapTime)}
                          </td>
                          <td
                            className={
                              lapData.powerMode === highPowerMode
                                ? styles.highPower
                                : undefined
                            }
                          >
                            {lapData.powerMode}
                          </td>
                          {virtualLoopNames.map((corner, i) => {
                            const cornerTime = lapData?.virtualLoops[corner];
                            let isFastest = false;
                            const isIn =
                              lapData.inLap &&
                              i === virtualLoopNames.length - 1 &&
                              !lastLoopNull;
                            const isOut = lapData.outLap && i === 0;
                            lastLoopNull = cornerTime === null;
                            if (
                              lapData.real !== false &&
                              typeof cornerTime === "number" &&
                              !isOut &&
                              !isIn &&
                              (fastestValues[corner] === undefined ||
                                cornerTime < fastestValues[corner])
                            ) {
                              fastestValues[corner] = cornerTime;
                              isFastest = true;
                            }

                            return (
                              <td
                                key={`${sessionKey}_lap${lapData.lap}_${corner}`}
                                className={`
                                ${
                                  !isGreyedOut && !colourMapEnabled && isFastest
                                    ? styles.fastestCorner
                                    : undefined
                                }
                                ${isIn ? styles.inLap : undefined}
                                ${isOut ? styles.outLap : undefined}
                                ${
                                  !isGreyedOut &&
                                  colourMapEnabled &&
                                  typeof cornerTime === "number" &&
                                  !isIn &&
                                  !isOut
                                    ? styles.colourMapped
                                    : undefined
                                }
                                ${isGreyedOut ? styles.greyedOutLap : ""}
                              `.trim()}
                                style={{
                                  backgroundColor:
                                    !isGreyedOut &&
                                    colourMapEnabled &&
                                    typeof cornerTime === "number" &&
                                    !isIn &&
                                    !isOut &&
                                    loopTimeRanges[corner]
                                      ? getColourMap(
                                          cornerTime,
                                          loopTimeRanges[corner].min,
                                          loopTimeRanges[corner].max,
                                        )
                                      : undefined,
                                }}
                              >
                                {isIn
                                  ? "IN"
                                  : isOut
                                  ? "OUT"
                                  : typeof cornerTime === "number"
                                  ? getPreferredFormattedTimeSeconds(cornerTime)
                                  : cornerTime}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LapsTable;
